import { supabase } from '../lib/supabase';
import type {
  RevenueStatistics,
  ProjectionsStatistics,
  RankedEntity,
  BrandPerformance,
  TimeSeriesDataPoint,
  UnprofitableCampaign,
  StatisticsFilters,
  RevenueTier,
  PerformanceBadge,
  RoiAssessment,
} from '../types/statistics';
import { getPaidReceivedAmount, getActualInfluencerPaidAmount, getEffectiveCommissionRate } from '../utils/paymentCalculations';
import { calculateInfluencerCommission, computeCampaignCommission, getCampaignTotalValue } from '../utils/commissionCalculations';
import { fetchIMAggregateByCampaign } from './campaignService';
import { log } from '../lib/logger';

const CONFIRMED_STATUSES = ['In Progress', 'Billing', 'Completed', 'Production'];

class StatisticsService {
  async getRevenueStatistics(filters?: StatisticsFilters): Promise<RevenueStatistics> {
    let query = supabase
      .from('campaigns')
      .select('id, campaign_mode, company_amount_received, influencer_amount_paid, campaign_status, created_at, company_invoice_status, influencer_invoice_status, mohit_invoices, influencer_invoices, fee, commission_pct, commission_type, commission_flat_fee, campaign_expenses, has_payables, sponsorship_amount, agency_commission_percent')
      .in('campaign_status', CONFIRMED_STATUSES);

    if (filters?.year) {
      query = query.gte('created_at', `${filters.year}-01-01`).lt('created_at', `${filters.year + 1}-01-01`);
    }

    const { data: campaigns, error } = await query;

    if (error || !campaigns) {
      log.error('Error fetching revenue statistics:', error);
      return {
        client_total: 0,
        influencer_total: 0,
        agency_commission: 0,
        net_margin: 0,
      };
    }

    const client_total = campaigns.reduce((sum, c: any) => {
      return sum + getPaidReceivedAmount(c);
    }, 0);
    const influencer_total = campaigns.reduce((sum, c: any) => {
      return sum + getActualInfluencerPaidAmount(c);
    }, 0);
    // Commission recognised on the cash actually received, at each campaign's
    // real rate. The old "client cash − creator cash" counted an unpaid
    // creator as 100% margin.
    const imAgg = await fetchIMAggregateByCampaign(
      campaigns.filter((c: any) => c.campaign_mode === 'influencer_marketing').map((c: any) => c.id)
    );
    const agency_commission = campaigns.reduce((sum, c: any) => {
      const agg = imAgg.get(c.id);
      const overrides = agg
        ? {
            totalNetEarnings: agg.im_total_fee - agg.im_total_commission,
            commissionRate: agg.im_total_fee > 0 ? agg.im_total_commission / agg.im_total_fee : 0,
          }
        : undefined;
      return sum + getPaidReceivedAmount(c) * getEffectiveCommissionRate(c, overrides);
    }, 0);
    const net_margin = client_total > 0 ? (agency_commission / client_total) * 100 : 0;

    return {
      client_total,
      influencer_total,
      agency_commission,
      net_margin,
    };
  }

  async getProjectionsStatistics(): Promise<ProjectionsStatistics> {
    const { data: campaigns, error } = await supabase
      .from('campaigns')
      .select('id, campaign_mode, fee, influencer_fee, commission_pct, commission_type, commission_flat_fee, campaign_expenses, has_payables, sponsorship_amount, agency_commission_percent, campaign_status')
      .eq('campaign_status', 'Consultation');

    if (error || !campaigns) {
      return {
        potential_client_revenue: 0,
        potential_influencer_costs: 0,
        potential_commission: 0,
        potential_margin: 0,
      };
    }

    // Same helpers as the dashboard: total invoiced value, commission by each
    // campaign's own rule (IM creators from their agreed fees), costs = the rest
    const imAgg = await fetchIMAggregateByCampaign(
      campaigns.filter((c: any) => c.campaign_mode === 'influencer_marketing').map((c: any) => c.id)
    );
    const potential_client_revenue = campaigns.reduce((sum, c: any) => {
      const agg = imAgg.get(c.id);
      return sum + (agg ? agg.im_total_fee : getCampaignTotalValue(c));
    }, 0);
    const potential_commission = campaigns.reduce((sum, c: any) => {
      const agg = imAgg.get(c.id);
      return sum + (agg ? agg.im_total_commission : computeCampaignCommission(c));
    }, 0);
    const potential_influencer_costs = potential_client_revenue - potential_commission;
    const potential_margin =
      potential_client_revenue > 0 ? (potential_commission / potential_client_revenue) * 100 : 0;

    return {
      potential_client_revenue,
      potential_influencer_costs,
      potential_commission,
      potential_margin,
    };
  }

  async getClientRevenueBreakdown(filters?: StatisticsFilters): Promise<RankedEntity[]> {
    let query = supabase
      .from('campaigns')
      .select('client_name, client, company_amount_received, company_invoice_status, campaign_status, created_at, mohit_invoices')
      .in('campaign_status', CONFIRMED_STATUSES);

    if (filters?.year) {
      query = query.gte('created_at', `${filters.year}-01-01`).lt('created_at', `${filters.year + 1}-01-01`);
    }

    const { data: campaigns, error } = await query;

    if (error || !campaigns) {
      return [];
    }

    const clientMap = new Map<string, { revenue: number; count: number }>();

    campaigns.forEach((campaign: any) => {
      const clientName = campaign.client_name || campaign.client;
      if (!clientName) return;

      const revenue = getPaidReceivedAmount(campaign);

      if (clientMap.has(clientName)) {
        const existing = clientMap.get(clientName)!;
        existing.revenue += revenue;
        existing.count += 1;
      } else {
        clientMap.set(clientName, { revenue, count: 1 });
      }
    });

    const clients = Array.from(clientMap.entries())
      .map(([name, data]) => ({
        id: name,
        name,
        value: data.revenue,
        campaign_count: data.count,
        average_value: data.count > 0 ? data.revenue / data.count : 0,
      }))
      .sort((a, b) => b.value - a.value);

    const totalRevenue = clients.reduce((sum, c) => sum + c.value, 0);

    return clients.map((client, index) => ({
      ...client,
      rank: index + 1,
      tier: this.getRevenueTier(client.value),
      contribution_percentage: totalRevenue > 0 ? (client.value / totalRevenue) * 100 : 0,
    }));
  }

  async getInfluencerRevenueBreakdown(filters?: StatisticsFilters): Promise<RankedEntity[]> {
    let campaignQuery = supabase
      .from('campaigns')
      .select('id, influencer_name, influencer, influencer_amount_paid, company_amount_received, influencer_invoice_status, company_invoice_status, campaign_status, created_at, campaign_mode, mohit_invoices, influencer_invoices')
      .in('campaign_status', CONFIRMED_STATUSES);

    if (filters?.year) {
      campaignQuery = campaignQuery.gte('created_at', `${filters.year}-01-01`).lt('created_at', `${filters.year + 1}-01-01`);
    }

    const { data: campaigns, error } = await campaignQuery;

    if (error || !campaigns) {
      return [];
    }

    const multiInfluencerCampaignIds = campaigns
      .filter((c: any) => c.campaign_mode === 'influencer_marketing')
      .map((c: any) => c.id);

    let campaignInfluencers: any[] = [];
    if (multiInfluencerCampaignIds.length > 0) {
      const { data: ciData } = await supabase
        .from('campaign_influencers')
        .select('campaign_id, influencer_name, agreed_fee, commission_pct, commission_flat_fee, commission_type')
        .in('campaign_id', multiInfluencerCampaignIds);
      campaignInfluencers = ciData || [];
    }

    const influencerMap = new Map<
      string,
      { fees: number; count: number; commission: number }
    >();

    campaigns.forEach((campaign: any) => {
      if (campaign.campaign_mode === 'influencer_marketing') {
        const ciForCampaign = campaignInfluencers.filter((ci: any) => ci.campaign_id === campaign.id);
        ciForCampaign.forEach((ci: any) => {
          const influencerName = ci.influencer_name;
          if (!influencerName) return;

          const fees = ci.agreed_fee || 0;
          const commission = calculateInfluencerCommission(
            fees,
            ci.commission_pct ?? 0,
            ci.commission_flat_fee ?? 0,
            ci.commission_type ?? 'percentage'
          );

          if (influencerMap.has(influencerName)) {
            const existing = influencerMap.get(influencerName)!;
            existing.fees += fees;
            existing.count += 1;
            existing.commission += commission;
          } else {
            influencerMap.set(influencerName, { fees, count: 1, commission });
          }
        });
      } else {
        const influencerName = campaign.influencer_name || campaign.influencer;
        if (!influencerName) return;

        const fees = getActualInfluencerPaidAmount(campaign);
        const clientRevenue = getPaidReceivedAmount(campaign);
        const commission = clientRevenue - fees;

        if (influencerMap.has(influencerName)) {
          const existing = influencerMap.get(influencerName)!;
          existing.fees += fees;
          existing.count += 1;
          existing.commission += commission;
        } else {
          influencerMap.set(influencerName, { fees, count: 1, commission });
        }
      }
    });

    const influencers = Array.from(influencerMap.entries())
      .map(([name, data]) => ({
        id: name,
        name,
        value: data.fees,
        campaign_count: data.count,
        average_value: data.count > 0 ? data.fees / data.count : 0,
        commission_generated: data.commission,
      }))
      .sort((a, b) => b.value - a.value);

    const totalFees = influencers.reduce((sum, i) => sum + i.value, 0);

    return influencers.map((influencer, index) => ({
      ...influencer,
      rank: index + 1,
      badge: this.getPerformanceBadge(influencer.campaign_count, index),
      delivery_rate: 95,
      contribution_percentage: totalFees > 0 ? (influencer.value / totalFees) * 100 : 0,
    }));
  }

  async getBrandPerformance(filters?: StatisticsFilters): Promise<BrandPerformance[]> {
    let query = supabase
      .from('campaigns')
      .select('brand, fee, campaign_status, delivery_status, created_at')
      .in('campaign_status', CONFIRMED_STATUSES);

    if (filters?.year) {
      query = query.gte('created_at', `${filters.year}-01-01`).lt('created_at', `${filters.year + 1}-01-01`);
    }

    const { data: campaigns, error } = await query;

    if (error || !campaigns) {
      return [];
    }

    const brandMap = new Map<
      string,
      { investment: number; count: number; successful: number; occurrences: number }
    >();

    campaigns.forEach((campaign: any) => {
      const brand = campaign.brand;
      if (!brand || brand.trim() === '') return;

      const investment = campaign.fee || 0;
      const isSuccessful = campaign.delivery_status === 'Published' || campaign.campaign_status === 'Completed';

      if (brandMap.has(brand)) {
        const existing = brandMap.get(brand)!;
        existing.investment += investment;
        existing.count += 1;
        existing.successful += isSuccessful ? 1 : 0;
        existing.occurrences += 1;
      } else {
        brandMap.set(brand, {
          investment,
          count: 1,
          successful: isSuccessful ? 1 : 0,
          occurrences: 1,
        });
      }
    });

    const brands = Array.from(brandMap.entries())
      .map(([brand, data]) => ({
        brand,
        total_investment: data.investment,
        campaign_count: data.count,
        success_rate: data.count > 0 ? (data.successful / data.count) * 100 : 0,
        is_repeat_client: data.occurrences > 1,
        roi_assessment: this.getRoiAssessment(data.successful / data.count),
      }))
      .sort((a, b) => b.total_investment - a.total_investment);

    const totalInvestment = brands.reduce((sum, b) => sum + b.total_investment, 0);

    return brands.map((brand, index) => ({
      ...brand,
      rank: index + 1,
      contribution_percentage: totalInvestment > 0 ? (brand.total_investment / totalInvestment) * 100 : 0,
    }));
  }

  async getTimeSeriesData(filters?: StatisticsFilters): Promise<TimeSeriesDataPoint[]> {
    let query = supabase
      .from('campaigns')
      .select('company_amount_received, influencer_amount_paid, company_invoice_status, influencer_invoice_status, campaign_status, created_at, mohit_invoices, influencer_invoices')
      .in('campaign_status', CONFIRMED_STATUSES)
      .order('created_at', { ascending: true });

    if (filters?.year) {
      query = query.gte('created_at', `${filters.year}-01-01`).lt('created_at', `${filters.year + 1}-01-01`);
    }

    const { data: campaigns, error } = await query;

    if (error || !campaigns) {
      return [];
    }

    const monthMap = new Map<
      string,
      { revenue: number; costs: number; count: number }
    >();

    campaigns.forEach((campaign: any) => {
      const date = new Date(campaign.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const revenue = getPaidReceivedAmount(campaign);
      const costs = getActualInfluencerPaidAmount(campaign);

      if (monthMap.has(monthKey)) {
        const existing = monthMap.get(monthKey)!;
        existing.revenue += revenue;
        existing.costs += costs;
        existing.count += 1;
      } else {
        monthMap.set(monthKey, { revenue, costs, count: 1 });
      }
    });

    const months = [
      'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
      'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
    ];

    return Array.from(monthMap.entries())
      .map(([date, data]) => {
        const [year, month] = date.split('-');
        const monthIndex = parseInt(month) - 1;
        return {
          date,
          month: `${months[monthIndex]} ${year}`,
          client_revenue: data.revenue,
          influencer_costs: data.costs,
          profit: data.revenue - data.costs,
          campaign_count: data.count,
        };
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  async getUnprofitableCampaigns(filters?: StatisticsFilters): Promise<UnprofitableCampaign[]> {
    let query = supabase
      .from('campaigns')
      .select('id, campaign_ref, brand, client, client_name, influencer, influencer_name, company_amount_received, influencer_amount_paid, company_invoice_status, influencer_invoice_status, campaign_status, created_at, mohit_invoices, influencer_invoices')
      .in('campaign_status', CONFIRMED_STATUSES);

    if (filters?.year) {
      query = query.gte('created_at', `${filters.year}-01-01`).lt('created_at', `${filters.year + 1}-01-01`);
    }

    const { data: campaigns, error } = await query;

    if (error || !campaigns) {
      return [];
    }

    return campaigns
      .map((c: any) => {
        const clientRevenue = getPaidReceivedAmount(c);
        const influencerCost = getActualInfluencerPaidAmount(c);
        const profit = clientRevenue - influencerCost;

        return {
          id: c.id,
          campaign_ref: c.campaign_ref || '-',
          brand: c.brand || '-',
          client: c.client_name || c.client || '-',
          influencer: c.influencer_name || c.influencer || '-',
          client_revenue: clientRevenue,
          influencer_cost: influencerCost,
          profit,
          campaign_status: c.campaign_status,
        };
      })
      .filter((c) => c.profit < 0)
      .sort((a, b) => a.profit - b.profit);
  }

  private getRevenueTier(revenue: number): RevenueTier {
    if (revenue >= 50000) return 'Platinum';
    if (revenue >= 25000) return 'Gold';
    if (revenue >= 10000) return 'Silver';
    return 'Bronze';
  }

  private getPerformanceBadge(campaignCount: number, rank: number): PerformanceBadge {
    if (rank === 0) return 'Top Performer';
    if (campaignCount >= 5) return 'Active';
    return 'Rising Star';
  }

  private getRoiAssessment(successRate: number): RoiAssessment {
    if (successRate >= 0.9) return 'Excellent';
    if (successRate >= 0.7) return 'Good';
    if (successRate >= 0.5) return 'Average';
    return 'Poor';
  }
}

export const statisticsService = new StatisticsService();
